import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import useRestaurants from "../hooks/useRestaurants";
import RestaurantCard from "./ui/RestaurantCard";
import { primaryCuisine } from "../utils/restoLink";
import { useLanguage } from "../contexts/LanguageContext";
import {
  haversineKm,
  readLastMapCenter,
  restoLngLat,
  writeLastMapCenter,
} from "../utils/geo";

const ALL = "__all__";

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { restaurants, loading, error } = useRestaurants();
  const [chip, setChip] = useState(ALL);
  const [here, setHere] = useState(readLastMapCenter);

  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const next = [pos.coords.longitude, pos.coords.latitude];
        if (Number.isFinite(next[0]) && Number.isFinite(next[1])) {
          writeLastMapCenter(next);
          setHere(next);
        }
      },
      () => {},
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 300000 },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const distKm = (r) => {
    if (!here) return Infinity;
    const c = restoLngLat(r);
    return c ? haversineKm(here, c) : Infinity;
  };

  const withPhotos = useMemo(
    () => (restaurants || []).filter((r) => r.picture),
    [restaurants],
  );

  const collage = withPhotos.slice(0, 3);
  const cuisines = useMemo(() => {
    const set = new Set();
    (restaurants || []).forEach((r) => {
      const c = primaryCuisine(r.cuisine);
      if (c) set.add(c);
    });
    return [ALL, ...Array.from(set).slice(0, 8)];
  }, [restaurants]);

  const curated = useMemo(() => {
    const list = restaurants || [];
    const filtered =
      chip === ALL
        ? list
        : list.filter((r) => primaryCuisine(r.cuisine) === chip);
    return [...filtered]
      .sort((a, b) => {
        const d = distKm(a) - distKm(b);
        if (d !== 0) return d;
        return Number(b.rating || 0) - Number(a.rating || 0);
      })
      .slice(0, 12);
  }, [restaurants, chip, here]);

  const nearby = useMemo(
    () =>
      [...(restaurants || [])]
        .sort((a, b) => {
          const d = distKm(a) - distKm(b);
          if (d !== 0) return d;
          return Number(b.rating || 0) - Number(a.rating || 0);
        })
        .slice(0, 8),
    [restaurants, here],
  );

  return (
    <Page>
      <Hero>
        <Copy>
          <Headline>{t("home.headline")}</Headline>
          <Sub>{t("home.sub")}</Sub>
          <Ctas>
            <button className="btn-ink" type="button" onClick={() => navigate("/favourites")}>
              {t("nav.startPool")}
            </button>
            <button className="btn-secondary" type="button" onClick={() => navigate("/map")}>
              {t("home.find")}
            </button>
          </Ctas>
        </Copy>
        {collage.length > 0 && (
          <Collage aria-hidden>
            {collage.map((r, i) => (
              <Polaroid key={r.name || i} $i={i}>
                <img
                  src={r.picture}
                  alt=""
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "low"}
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              </Polaroid>
            ))}
          </Collage>
        )}
      </Hero>

      <SearchRow>
        <SearchBtn type="button" onClick={() => navigate("/map")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
            <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          {t("home.searchHood")}
        </SearchBtn>
      </SearchRow>

      <PoolBanner type="button" onClick={() => navigate("/favourites")}>
        <div>
          <strong>{t("home.poolTitle")}</strong>
          <span>{t("home.poolSub")}</span>
        </div>
        <Yellow>{t("nav.startPool")}</Yellow>
      </PoolBanner>

      {loading && !error && curated.length === 0 && (
        <Section aria-hidden>
          <SectionHead>
            <h3>{t("home.curated")}</h3>
          </SectionHead>
          <Scroller>
            {[0, 1, 2, 3].map((i) => (
              <SkeletonCard key={i} $variant="scroll">
                <SkeletonThumb />
                <SkeletonLine $w="72%" />
                <SkeletonLine $w="46%" />
              </SkeletonCard>
            ))}
          </Scroller>
        </Section>
      )}

      {!loading && !error && curated.length > 0 && (
        <Section>
          <SectionHead>
            <h3>{t("home.curated")}</h3>
          </SectionHead>
          <Chips>
            {cuisines.map((c) => (
              <Chip
                key={c}
                type="button"
                className={chip === c ? "is-active" : "chip"}
                onClick={() => setChip(c)}
              >
                {c === ALL ? t("home.all") : c}
              </Chip>
            ))}
          </Chips>
          <Scroller>
            {curated.map((resto, index) => (
              <RestaurantCard
                key={resto.restaurantId || resto.name || index}
                restaurant={resto}
                variant="scroll"
              />
            ))}
          </Scroller>
        </Section>
      )}

      {!loading && !error && nearby.length > 0 && (
        <Section>
          <SectionHead>
            <h3>{t("home.tonight")}</h3>
          </SectionHead>
          <Grid>
            {nearby.map((resto, index) => (
              <RestaurantCard
                key={`near-${resto.restaurantId || resto.name || index}`}
                restaurant={resto}
              />
            ))}
          </Grid>
        </Section>
      )}
    </Page>
  );
};

export default HomePage;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 0 0 220px;
  width: 220px;
`;

const shimmer = `
  background: currentColor;
  opacity: 0.09;
  border-radius: var(--radius);
  animation: gourmeto-pulse 1.4s ease-in-out infinite;

  @keyframes gourmeto-pulse {
    0%, 100% { opacity: 0.07; }
    50% { opacity: 0.14; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkeletonThumb = styled.div`
  ${shimmer}
  width: 100%;
  aspect-ratio: 4 / 3;
`;

const SkeletonLine = styled.div`
  ${shimmer}
  height: 12px;
  border-radius: 6px;
  width: ${(p) => p.$w || "100%"};
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px 0 36px;
  color: inherit;
`;

const Hero = styled.section`
  padding: 12px 18px 8px;
  display: grid;
  gap: 20px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
    padding: 36px 32px 12px;
    min-height: 420px;
  }
`;

const Copy = styled.div``;

const Headline = styled.h1`
  font-size: clamp(34px, 7vw, 64px);
  line-height: 1.05;
  margin-bottom: 12px;
  font-weight: 600;
  letter-spacing: -0.04em;
`;

const Sub = styled.p`
  color: var(--text-on-dark-muted);
  font-size: 15px;
  line-height: 1.5;
  max-width: 36ch;
  margin-bottom: 20px;

  @media (min-width: 1024px) {
    color: var(--text-secondary);
    font-size: 16px;
  }
`;

const Ctas = styled.div`
  display: none;
  flex-wrap: wrap;
  gap: 8px;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const Collage = styled.div`
  display: none;
  position: relative;
  height: 360px;

  @media (min-width: 1024px) {
    display: block;
  }
`;

const Polaroid = styled.div`
  position: absolute;
  width: 58%;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(15, 15, 16, 0.12);
  transform: ${(p) =>
    p.$i === 0
      ? "rotate(-8deg) translate(8%, 18%)"
      : p.$i === 1
        ? "rotate(4deg) translate(48%, 0%)"
        : "rotate(-2deg) translate(28%, 42%)"};
  z-index: ${(p) => 3 - p.$i};

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    display: block;
  }
`;

const SearchRow = styled.div`
  padding: 8px 18px 4px;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const SearchBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 16px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-on-dark);
  background: var(--surface-dark-2);
  color: var(--text-on-dark-muted);
  font-size: 14px;
`;

const PoolBanner = styled.button`
  margin: 12px 18px;
  width: calc(100% - 36px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
  border: none;
  background: var(--surface-dark-2);
  color: var(--text-on-dark);
  border-radius: var(--radius-lg);
  padding: 16px;

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 18px;
    margin-bottom: 4px;
  }

  span {
    display: block;
    font-size: 13px;
    color: var(--text-on-dark-muted);
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const Yellow = styled.span`
  flex-shrink: 0;
  background: var(--color-yellow);
  color: var(--color-ink-solid);
  border-radius: var(--radius-pill);
  padding: 10px 14px;
  font-weight: 700;
  font-size: 13px;
`;

const Section = styled.section`
  padding: 18px 18px 0;

  @media (min-width: 1024px) {
    padding: 28px 32px 0;
  }
`;

const SectionHead = styled.div`
  margin-bottom: 12px;

  h3 {
    font-size: 22px;
    color: inherit;
  }

  @media (min-width: 1024px) {
    h3 {
      font-size: 28px;
    }
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 14px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Chip = styled.button`
  flex: 0 0 auto;
  border-radius: var(--radius-pill);
  padding: 8px 14px;
  border: 1px solid var(--border-on-dark);
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 500;

  &.is-active {
    background: var(--color-yellow);
    border-color: var(--color-yellow);
    color: var(--color-ink-solid);
  }

  @media (min-width: 1024px) {
    border-color: var(--line);
    color: inherit;

    &.is-active {
      background: var(--color-yellow);
      border-color: var(--color-yellow);
      color: var(--color-ink-solid);
    }
  }
`;

const Scroller = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  a, button {
    color: inherit;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;
